import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MenuForm from "./MenuForm";
import MenuSetupService from "../../../services/KangarooServices/MenuSetupService";
import DeleteModal from "../../../Components/Common/DeleteModal";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

const MenuColumn = ({
  menus,
  onUpdate,
  onReorder,
  fetchMenu,
  selectedTypeId,
  typeData,
  menuData,
  fetchType,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [reorderedFlat, setReorderedFlat] = useState([]);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [editMenu, setEditMenu] = useState(null);
  console.log("🚀 ~ MenuColumn ~ editMenu:", editMenu);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const navigate = useNavigate();
  // Flatten hierarchical menus
  const flattenMenus = (list, parentId = null, inheritedMenuTypeId = null) => {
    const flat = [];
    list.forEach((item) => {
      const menuTypeId = item.menuTypeId || inheritedMenuTypeId;
      const { children, ...rest } = item;

      flat.push({
        ...rest,
        parentId: parentId || null,
        menuTypeId, // Ensure it's preserved
      });

      if (children?.length > 0) {
        flat.push(...flattenMenus(children, item.id, menuTypeId));
      }
    });
    return flat;
  };
  // Expand / collapse menu
  const toggleExpand = (menuId, e) => {
    e.stopPropagation();
    setExpandedMenus((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) newSet.delete(menuId);
      else newSet.add(menuId);
      return newSet;
    });
  };

  // Build tree from flat list
  const buildTreeFromFlat = (flatList) => {
    const map = new Map(
      flatList.map((item) => [item.id, { ...item, children: [] }])
    );
    const roots = [];
    flatList.forEach((item) => {
      const node = map.get(item.id);
      if (item.parentId === null) roots.push(node);
      else {
        const parent = map.get(item.parentId);
        if (parent) parent.children.push(node);
      }
    });

    const sortNodes = (nodes) => {
      nodes.sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0));
      nodes.forEach((node) => node.children && sortNodes(node.children));
    };
    sortNodes(roots);

    return roots;
  };

  // Payload generator for API
  // Improved buildPayload – ensures ALL fields are always included
  const buildPayload = (item) => ({
    id: item.id,
    title: item.title || "",
    url: item.url || "",
    isExternal: item.isExternal ?? false,
    parentId: item.parentId || null,
    menuTypeId: item.menuTypeId || null,
    orderIndex: Number(item.orderIndex ?? 0),
    isActive: item.isActive ?? true,
  });

  useEffect(() => {
    const flat = flattenMenus(menus);
    setReorderedFlat(flat);

    // Auto-expand parents with children
    const parents = new Set();
    menus.forEach((m) => {
      if (m.children?.length > 0) parents.add(m.id);
    });
    setExpandedMenus(parents);
  }, [menus]);

  const tree = editMode ? buildTreeFromFlat(reorderedFlat) : menus;

  // Drag-and-drop handler
  const handleDragEnd = (result) => {
    if (!result.destination || !editMode) return;

    const { draggableId, source, destination } = result;
    const items = Array.from(reorderedFlat);
    const draggedIndex = items.findIndex((i) => i.id === draggableId);
    const [draggedItem] = items.splice(draggedIndex, 1);

    // Determine new parent
    const newParentId =
      destination.droppableId === "root"
        ? null
        : destination.droppableId.replace("parent-", "");
    draggedItem.parentId = newParentId;

    // Re-insert at new position
    items.splice(destination.index, 0, draggedItem);

    // Recalculate orderIndex per parent
    const getParentGroup = (parentId) =>
      items.filter((i) => (i.parentId || null) === (parentId || null));
    const oldParentId =
      source.droppableId === "root"
        ? null
        : source.droppableId.replace("parent-", "");
    getParentGroup(oldParentId).forEach((item, idx) => (item.orderIndex = idx));
    getParentGroup(newParentId).forEach((item, idx) => (item.orderIndex = idx));

    setReorderedFlat(items);
  };

  const saveOrder = async () => {
    const originalFlat = flattenMenus(menus);

    // Find items where parentId or orderIndex changed
    const changedItems = reorderedFlat.filter((current) => {
      const original = originalFlat.find((o) => o.id === current.id);
      if (!original) return true;

      return (
        (original.parentId || null) !== (current.parentId || null) ||
        (original.orderIndex ?? 0) !== (current.orderIndex ?? 0)
      );
    });

    if (changedItems.length > 0) {
      // Map to FULL payload using the improved buildPayload
      const payloads = changedItems.map(buildPayload);

      try {
        await onReorder(payloads); // This already uses Promise.all in parent
        toast.success("Menu order updated successfully");
        setEditMode(false);
      } catch (error) {
        toast.error("Failed to save order");
        console.error(error);
      }
    } else {
      toast.info("No changes detected");
      setEditMode(false);
    }
  };

  // Single menu card component
  const MenuItemCard = ({ menu, provided, snapshot, isChild = false }) => {
    const hasChildren = menu.children?.length > 0;
    const isExpanded = expandedMenus.has(menu.id);

    return (
      <div
        ref={provided?.innerRef}
        {...(provided?.draggableProps || {})}
        style={{ ...provided?.draggableProps?.style }}
        className={`menu-item-card rounded border mb-2 ${
          snapshot?.isDragging
            ? "shadow-lg border-primary"
            : isChild
            ? "border-light"
            : "shadow-sm"
        }`}
      >
        <div className="d-flex align-items-center p-3">
          {editMode && (
            <div
              {...(provided?.dragHandleProps || {})}
              className="me-2"
              style={{ cursor: "grab" }}
            >
              <i className="ri-drag-move-2-line text-muted fs-5" />
            </div>
          )}

          {!editMode && hasChildren && (
            <button
              className="btn btn-sm btn-link p-0 me-2 text-primary"
              onClick={(e) => toggleExpand(menu.id, e)}
            >
              <i
                className={`ri-arrow-${
                  isExpanded ? "down" : "right"
                }-s-line fs-5`}
              />
            </button>
          )}

          <div className="flex-grow-1">
            <div className="d-flex align-items-center gap-2 mb-1">
              <strong>{menu.title}</strong>
              {menu.isExternal && (
                <span className="badge bg-info bg-opacity-10 text-info">
                  External
                </span>
              )}
              {!menu.isActive && (
                <span className="badge bg-secondary bg-opacity-10 text-secondary">
                  Inactive
                </span>
              )}
            </div>
            <div className="text-muted small">{menu.url}</div>
          </div>

          <div className="d-flex gap-2 ms-2">
            <Button
              size="sm"
              color="warning"
              outline
              // onClick={() => setEditMenu({ ...menu })}
              onClick={() => navigate(`/menu/update/${menu?.id}`)}
              title="Edit"
            >
              <i className="ri-edit-line" />
            </Button>
            <Button
              size="sm"
              color="danger"
              outline
              onClick={() => {
                setDeleteItem(menu);
                setShowDelete(true);
              }}
              title="Delete"
            >
              <i className="ri-delete-bin-line" />
            </Button>
          </div>
        </div>

        {hasChildren && (isExpanded || editMode) && (
          <div
            className="children-section border-top"
            style={{ marginLeft: isChild ? "0" : "40px" }}
          >
            <Droppable droppableId={`parent-${menu.id}`} type="CHILD">
              {(provided) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className="p-3"
                >
                  {menu.children.length === 0 && editMode && (
                    <div className="text-center py-3 border border-dashed rounded text-muted">
                      <i className="ri-drag-drop-line fs-4 d-block mb-2" />
                      <small>Drop submenu items here</small>
                    </div>
                  )}
                  {menu.children.map((child, idx) => (
                    <Draggable
                      key={child.id}
                      draggableId={child.id}
                      index={idx}
                      isDragDisabled={!editMode}
                    >
                      {(childProvided, childSnapshot) => (
                        <MenuItemCard
                          menu={child}
                          provided={childProvided}
                          snapshot={childSnapshot}
                          isChild
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="shadow-sm">
      <CardHeader className="d-flex justify-content-between align-items-center bg-white">
        <h5 className="m-0">
          Menus{" "}
          {selectedTypeId ? (
            <span className="badge bg-primary bg-opacity-10 text-primary ms-2">
              {typeData.find((t) => t.id === selectedTypeId)?.name || ""}
            </span>
          ) : (
            <span className="badge bg-secondary bg-opacity-10 text-secondary ms-2">
              All Types
            </span>
          )}
        </h5>
        <div className="d-flex gap-2">
          {!editMode && tree.length > 0 && (
            <Button
              size="sm"
              color="info"
              outline
              onClick={() => {
                const allIds = new Set();
                tree.forEach((m) => {
                  if (m.children?.length > 0) allIds.add(m.id);
                });
                setExpandedMenus(allIds);
              }}
            >
              <i className="ri-expand-up-down-line me-1" /> Expand All
            </Button>
          )}
          {tree.length > 0 && (
            <Button
              size="sm"
              color={editMode ? "success" : "primary"}
              onClick={editMode ? saveOrder : () => setEditMode(true)}
            >
              <i
                className={`ri-${editMode ? "save" : "drag-move-2"}-line me-1`}
              />{" "}
              {editMode ? "Save Order" : "Reorder"}
            </Button>
          )}
          {editMode && (
            <Button
              size="sm"
              color="secondary"
              outline
              onClick={() => {
                setReorderedFlat(flattenMenus(menus));
                setEditMode(false);
              }}
            >
              <i className="ri-close-line me-1" /> Cancel
            </Button>
          )}
          <Button
            size="sm"
            color="success"
            onClick={() => navigate("/menu/create")}
          >
            <i className="ri-add-line me-1" /> Add Menu
          </Button>
        </div>
      </CardHeader>

      <CardBody className="p-4">
        {tree.length === 0 ? (
          <div className="text-center py-5">
            <div className="mb-4">
              <i className="ri-file-list-3-line display-1 text-muted opacity-50" />
            </div>
            <h5 className="text-muted mb-3">No menus found</h5>
            <Button color="primary" onClick={() => setIsFormOpen(true)}>
              <i className="ri-add-circle-line me-1" /> Create First Menu
            </Button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="root" type="PARENT">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {tree.map((menu, idx) => (
                    <Draggable
                      key={menu.id}
                      draggableId={menu.id}
                      index={idx}
                      isDragDisabled={!editMode}
                    >
                      {(provided, snapshot) => (
                        <MenuItemCard
                          menu={menu}
                          provided={provided}
                          snapshot={snapshot}
                        />
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </CardBody>

      <MenuForm
        isOpen={isFormOpen}
        toggle={() => setIsFormOpen(false)}
        fetchMenu={fetchMenu}
        selectedTypeId={selectedTypeId}
        menuData={menuData}
        typeData={typeData}
        fetchType={fetchType}
      />

      <DeleteModal
        show={showDelete}
        onCloseClick={() => setShowDelete(false)}
        onDeleteClick={async () => {
          if (deleteItem) {
            await MenuSetupService.delete(deleteItem.id);
            toast.success("Menu deleted successfully");
            fetchMenu();
          }
          setShowDelete(false);
          setDeleteItem(null);
        }}
      />

      {editMenu && (
        <Modal isOpen={!!editMenu} toggle={() => setEditMenu(null)} centered>
          <ModalHeader toggle={() => setEditMenu(null)}>Edit Menu</ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Title</Label>
              <Input
                value={editMenu.title}
                onChange={(e) =>
                  setEditMenu({ ...editMenu, title: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>Order Index</Label>
              <Input
                type="number"
                value={editMenu.orderIndex}
                onChange={(e) =>
                  setEditMenu({ ...editMenu, orderIndex: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup>
              <Label>URL</Label>
              <Input
                value={editMenu.url}
                onChange={(e) =>
                  setEditMenu({ ...editMenu, url: e.target.value })
                }
              />
            </FormGroup>
            <FormGroup className="form-check form-switch">
              <Input
                type="checkbox"
                id="isExternal"
                className="form-check-input"
                checked={editMenu.isExternal || false}
                onChange={(e) =>
                  setEditMenu({ ...editMenu, isExternal: e.target.checked })
                }
              />
              <Label htmlFor="isExternal" className="form-check-label">
                External Link
              </Label>
            </FormGroup>
            <FormGroup className="form-check form-switch">
              <Input
                type="checkbox"
                id="isActive"
                className="form-check-input"
                checked={editMenu.isActive ?? true}
                onChange={(e) =>
                  setEditMenu({ ...editMenu, isActive: e.target.checked })
                }
              />
              <Label htmlFor="isActive" className="form-check-label">
                Active
              </Label>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={() => {
                const payload = buildPayload(editMenu);
                onUpdate(payload); // ← This is correct
                setEditMenu(null);
              }}
            >
              Save Changes
            </Button>
            <Button color="secondary" onClick={() => setEditMenu(null)}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Card>
  );
};

export default MenuColumn;
